/**
 * AccountSettingsContext — Global open/close state for the Account Settings modal.
 *
 * The modal is rendered at the App level so it can be opened from anywhere
 * (avatar menu, profile page, milestone CTAs, etc.) without navigating away
 * from the current page.
 *
 * `openToTab(tab)` lets callers deep-link to a specific tab — used by the
 * `enable_2fa` milestone CTA to land directly on Privacy & Security where
 * the TotpSetup component lives.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

export type AccountSettingsTab = 'profile' | 'app' | 'privacy';

const TAB_INDEX: Record<AccountSettingsTab, 0 | 1 | 2> = {
  profile: 0,
  app: 1,
  privacy: 2,
};

interface AccountSettingsContextValue {
  isOpen: boolean;
  /** Tab to open to. Set via `openToTab`; defaults to 'profile' on plain `open()`. */
  initialTab: 0 | 1 | 2;
  /**
   * The active "viewed-as" user GUID — the user whose settings the modal
   * should display. App.tsx pushes this in via `setActiveUserGuid` whenever
   * its `currentUserGuid` state changes (login, gear-menu switch, real
   * impersonation). This decouples the modal from `getCurrentUserGuid()`
   * in api/client, which has different priority semantics and would
   * return the JWT-authenticated user instead of the gear-menu-selected
   * user during a debug user-switch.
   */
  activeUserGuid: string;
  open: () => void;
  openToTab: (tab: AccountSettingsTab) => void;
  close: () => void;
  /** Called by App.tsx to keep the context in sync with its currentUserGuid state. */
  setActiveUserGuid: (guid: string) => void;
}

const AccountSettingsContext = createContext<AccountSettingsContextValue>({
  isOpen: false,
  initialTab: 0,
  activeUserGuid: '',
  open: () => {},
  openToTab: () => {},
  close: () => {},
  setActiveUserGuid: () => {},
});

export function AccountSettingsProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<0 | 1 | 2>(0);
  const [activeUserGuid, setActiveUserGuid] = useState<string>('');

  const open = useCallback(() => {
    setInitialTab(0);
    setIsOpen(true);
  }, []);

  const openToTab = useCallback((tab: AccountSettingsTab) => {
    setInitialTab(TAB_INDEX[tab]);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <AccountSettingsContext.Provider
      value={{
        isOpen,
        initialTab,
        activeUserGuid,
        open,
        openToTab,
        close,
        setActiveUserGuid,
      }}
    >
      {children}
    </AccountSettingsContext.Provider>
  );
}

export function useAccountSettings() {
  return useContext(AccountSettingsContext);
}
